'use client'

import { useState } from 'react'
import Image from 'next/image'

type FallbackSpec = {
  className: string
  lines: string[]
}

const FALLBACK_SPECS: Record<string, FallbackSpec> = {
  'https://logo.clearbit.com/ggg.com.vn': {
    className: 'text-xl font-black tracking-tighter text-charcoal-800',
    lines: ['GOLDEN GATE'],
  },
  'https://logo.clearbit.com/highlandscoffee.com.vn': {
    className: 'text-lg font-mono font-bold tracking-widest text-brand-red',
    lines: ['HIGHLANDS'],
  },
  'https://logo.clearbit.com/vingroup.net': {
    className: 'text-xl font-bold tracking-tight text-[#1D4ED8]',
    lines: ['VINGROUP'],
  },
  'https://logo.clearbit.com/sungroup.com.vn': {
    className: 'text-xl font-semibold uppercase tracking-wide text-[#EAB308]',
    lines: ['Sungroup'],
  },
  'https://logo.clearbit.com/goldsunfood.vn': {
    className: 'text-lg font-serif italic font-bold text-charcoal-800',
    lines: ['Goldsun Food'],
  },
  'https://logo.clearbit.com/qsrvietnam.com': {
    className: 'text-lg font-mono font-bold text-brand-red',
    lines: ['QSR VIETNAM'],
  },
  'https://logo.clearbit.com/burgerking.vn': {
    className: 'text-xl font-black uppercase text-[#CA8A04]',
    lines: ['Burger King'],
  },
  'https://logo.clearbit.com/thepizzacompany.vn': {
    className: 'text-center text-fs-2xl font-bold uppercase leading-tight tracking-widest text-[#15803D]',
    lines: ['The Pizza Company'],
  },
  'https://logo.clearbit.com/centralretail.com.vn': {
    className: 'text-xl font-bold uppercase tracking-tight text-[#DC2626]',
    lines: ['Central Group'],
  },
  'https://logo.clearbit.com/popeyes.vn': {
    className: 'text-lg font-bold uppercase text-[#F97316]',
    lines: ['Popeyes'],
  },
  'https://logo.clearbit.com/vlotus.vn': {
    className: 'text-lg font-serif font-bold text-charcoal-800',
    lines: ['V Lotus Group'],
  },
  'https://logo.clearbit.com/vfbs.vn': {
    className: 'text-xl font-mono font-bold tracking-widest text-charcoal-800',
    lines: ['VFBS'],
  },
  'https://logo.clearbit.com/global-x.vn': {
    className: 'text-xl font-black text-charcoal-800',
    lines: ['GLOBAL-X'],
  },
  'https://logo.clearbit.com/redwok.com.vn': {
    className: 'text-xl font-bold uppercase tracking-widest text-brand-red',
    lines: ['RED WOK'],
  },
  'https://logo.clearbit.com/cheesecoffee.vn': {
    className: 'text-center text-lg font-mono font-bold leading-tight text-charcoal-800',
    lines: ['CHEESE', 'COFFEE'],
  },
  'https://logo.clearbit.com/caferunam.com': {
    className: 'text-2xl font-serif italic text-charcoal-800',
    lines: ['Ru Nam'],
  },
  'https://logo.clearbit.com/elgaucho.asia': {
    className: 'text-xl font-bold uppercase tracking-wide text-charcoal-800',
    lines: ['El Gaucho'],
  },
  'https://logo.clearbit.com/hoangyengroup.com': {
    className: 'text-center text-lg font-semibold uppercase leading-tight text-brand-red',
    lines: ['Hoang Yen', 'Group'],
  },
  'https://logo.clearbit.com/maisonmarou.com': {
    className: 'text-center text-fs-2xl font-bold uppercase leading-tight tracking-tighter text-charcoal-800',
    lines: ['Marou', 'Chocolate'],
  },
}

export function LogoTileImage({ src, alt }: { src: string; alt: string }) {
  const [hasError, setHasError] = useState(false)
  const fallback = FALLBACK_SPECS[src] ?? {
    className: 'text-xl font-black tracking-tighter text-charcoal-800',
    lines: [alt.toUpperCase()],
  }

  if (hasError) {
    return (
      <span className={`cursor-default ${fallback.className}`}>
        {fallback.lines.map((line, index) => (
          <span key={`${line}-${index}`}>
            {line}
            {index < fallback.lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </span>
    )
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={220}
      height={64}
      className="h-auto max-h-16 w-auto object-contain"
      onError={() => setHasError(true)}
    />
  )
}
